import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AutofillOptions,
    IAnnualReport,
    IAnnualReportPdfRequest,
    IFinStatements,
    IFinancialStatementSource,
    IPrintOptions,
    ISetupData,
} from '@modeso/types__aumico-lib-annualreport-be';

import { ConfigurationsProvider, ReportType } from '@modeso/aumico-lib-core-fe';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, finalize, map, mergeMap, skip, switchMap, tap } from 'rxjs/operators';
import * as fromAnnualReportAction from '../actions/annualReport.actions';
import { TAXONAMY } from '../constants/autofill-options';
import { AnnualReportMetaInformationDTO } from '../dtos/annualReportMetaInformation.dto';
import { IDispatchAnnualReportsFilter } from '../dtos/dispatch-annual-reports-filter.dto';
import { IAuditLogDTO } from '../dtos/get-audit-log.dto';
import { PrintUsingPdfTronDto } from '../dtos/print-using-pdf-tron.dto';
import { applyReclassificationsToFinStatements } from '../helpers/applyReclassificationsToFinStatements';
import { markReclassMaterialized } from '../helpers/reclassMaterializedMarker';
import { DueDates } from '../interfaces/dueDate.interface';
import { AnnualReportExcelSheet } from '../interfaces/export-excel/annual-report-excel-sheet.interface';
import { FeatureState } from '../reducer/annualReport.reducer';
import * as fromFeature from './../reducer/annualReport.reducer';
import { IAnnualReportProvider } from './annualReport.provider.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:AnnualReportProvider');

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AnnualReportProvider implements IAnnualReportProvider {
    private activeAnnualReportRequestDispatched: boolean;

    constructor(protected store: Store<FeatureState>, protected configuration: ConfigurationsProvider) {
        debug('provider constructed');
        if (configuration.isStorybook()) {
            console.log('AnnualReportProvider, Config is Mocked 0');
        }
    }
    public dispatchSealAnnualReport(annualReportId: string): void {
        this.store.dispatch(fromAnnualReportAction.sealAnnualReport({ payload: annualReportId }));
    }
    public dispatchUnsealAnnualReport(annualReportId: string): void {
        this.store.dispatch(fromAnnualReportAction.unsealAnnualReport({ payload: annualReportId }));
    }
    public dispatchUpdatePrintOptions(annualReportEncryptedId: string, printOptions: IPrintOptions): void {
        this.store.dispatch(
            fromAnnualReportAction.updatePrintOptions({ payload: { annualReportEncryptedId, printOptions } }),
        );
    }

    public dispatchGetReportSealHistory(annualReportEncryptedId: string): void {
        this.store.dispatch(fromAnnualReportAction.getAnnualReportSealHistory({ payload: annualReportEncryptedId }));
    }

    public getReportSealHistory$(): Observable<IAuditLogDTO[]> {
        return this.store.pipe(select(fromFeature.selectAnnualReportAuditLogs));
    }

    public getPlaceholdersValues$(): Observable<IDynamicKeyValue> {
        return this.store.pipe(select(fromFeature.selectPlaceholderMap));
    }

    public dispatchUpdatePlaceholderUserData(userName: string): void {
        this.store.dispatch(fromAnnualReportAction.updateUserPlaceholderData({ userName }));
    }

    public dispatchUpdatePlaceholderOrganizationData(organization: GetOrganizationDto): void {
        this.store.dispatch(fromAnnualReportAction.updateOrganizationPlaceholderData({ organization }));
    }

    public dispatchUpdateAopSnapshotPlaceholder(aopSnapshot: string): void {
        this.store.dispatch(fromAnnualReportAction.updateAopSnapshotPlaceholder({ aopSnapshot }));
    }

    public dispatchUpdateAopSnapshotCurrentYearPlaceholder(aopSnapshotCurrentYear: string): void {
        this.store.dispatch(fromAnnualReportAction.updateAopSnapshotCurrentYearPlaceholder({ aopSnapshotCurrentYear }));
    }

    public createAnnualReport(
        setupData: ISetupData,
        existingAnnualReportEncryptedId?: string,
        existingProjectId?: string,
    ): void {
        this.store.dispatch(
            fromAnnualReportAction.createAnnualReport({
                payload: { setupData, existingAnnualReportEncryptedId, existingProjectId },
            }),
        );
    }

    public dispatchUpdateAnnualReport(annualReport: IAnnualReport, annualReportEncryptedId: string): void {
        this.store.dispatch(
            fromAnnualReportAction.updateAnnualReport({ payload: { annualReport, annualReportEncryptedId } }),
        );
    }

    public dispatchEnforceStateUpdateForAnnualReport(annualReport: IAnnualReport): void {
        this.store.dispatch(
            fromAnnualReportAction.enforceStateUpdateForAnnualReport({ payload: { annualReport } }),
        );
    }

    // Attention: This method shall only be used if you modify a fin statement after the FCP
    // Never use this method in steps before the FCP!
    public dispatchUpdateFinStatements(finStatements: IFinStatements[], annualReportEncryptedId: string): void {
        this.store.dispatch(
            fromAnnualReportAction.updateFinStatementsOfAnnualReport({
                payload: { finStatements, annualReportEncryptedId },
            }),
        );
    }

    public dispatchPrintAnnualReport(print: IAnnualReportPdfRequest): void {
        this.store.dispatch(fromAnnualReportAction.printAnnualReport({ payload: print }));
    }

    public dispatchPrintAnnualReportUsingPdfTron(printUsingPdfTronRequestData: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTron({
                payload: printUsingPdfTronRequestData,
            }),
        );
    }

    public dispatchPrintAnnualReportUsingPdfTronV2(printUsingPdfTronRequestData: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.printAnnualReportUsingPdfTronV2.printAnnualReportUsingPdfTronV2({
                payload: printUsingPdfTronRequestData,
            }),
        );
    }

    public dispatchExportAnnualReportToWord(dataToExport: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportToWord.exportAnnualReportToWord({ payload: dataToExport }),
        );
    }

    public dispatchExportAnnualReportToWordV2(dataToExport: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportToWordV2.exportAnnualReportToWordV2({ payload: dataToExport }),
        );
    }

    public dispatchExportAnnualReportExcel(dataToExport: AnnualReportExcelSheet[]): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportExcel.exportAnnualReportExcel({
                payload: { sheets: dataToExport },
            }),
        );
    }

    public dispatchExportAnnualReportExcelV2(dataToExport: AnnualReportExcelSheet[]): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportExcelV2.exportAnnualReportExcelV2({
                payload: { sheets: dataToExport },
            }),
        );
    }

    public dispatchExportAnnualReportExcelStructured(dataToExport: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportToExcelStructured.exportAnnualReportToExcelStructured({
                payload: dataToExport,
            }),
        );
    }

    public dispatchExportAnnualReportExcelStructuredV2(dataToExport: PrintUsingPdfTronDto): void {
        this.store.dispatch(
            fromAnnualReportAction.exportAnnualReportToExcelStructuredV2.exportAnnualReportToExcelStructuredV2({
                payload: dataToExport,
            }),
        );
    }

    public getActiveAnnualReport$(): Observable<IAnnualReport> {
        return this.store.pipe(
            select(fromFeature.selectActiveAnnualReport),
            switchMap((activeAnnualReport) => {
                if (activeAnnualReport == null && !this.activeAnnualReportRequestDispatched) {
                    this.activeAnnualReportRequestDispatched = true;
                    return this.store.pipe(
                        select(fromFeature.selectActiveAnnualReportEncryptedId),
                        filter((encryptedId) => !!encryptedId),
                        tap((encryptedId) => {
                            this.dispatchActiveAnnualReport(encryptedId);
                        }),
                        map(() => null),
                    );
                }
                return of(activeAnnualReport);
            }),
            filter((activeAnnualReport) => activeAnnualReport != null),
            switchMap((activeAnnualReport) => of(activeAnnualReport)),
            tap((activeAnnualReport: IAnnualReport) => {
                const uploadAndMappingAutoFillAnnualReportId =
                    activeAnnualReport.templateReportAutofillOptions?.[TAXONAMY];

                if (uploadAndMappingAutoFillAnnualReportId) {
                    this.dispatchGetAnnualReportForMappingAutoFill(uploadAndMappingAutoFillAnnualReportId);
                }

                const autoFillAnnualReportId = activeAnnualReport.autoFillAnnualReportId;

                if (autoFillAnnualReportId) {
                    this.dispatchBalanceAutoFillFromChosenOrSavedId(autoFillAnnualReportId);
                }
            }),
            finalize(() => (this.activeAnnualReportRequestDispatched = false)),
        );
    }

    /**
     * REV-20899: same as `getActiveAnnualReport$()` but materializes the
     * transactional reclassifications onto each fin statement's per-account
     * `openingReClass` / `closingReClass` fields.
     *
     * Use this selector from every annual-report step that needs to *show*
     * reclass-aware values (Grouping/Renaming, Cashflow, Notes, AoP, Equity
     * Movement, Fixed Assets, Print Preview, PDF mappers). Do NOT use it from
     * Upload or Account-Mapping — those steps must show the raw uploaded
     * balances exactly as imported.
     *
     * - No-op when `reclassificationsVersion !== 1` (returns input untouched).
     * - Otherwise deep-clones `finStatements`, applies the helper, leaves the
     *   rest of the AR by reference. Cached per source AR via WeakMap so
     *   re-emissions are cheap.
     */
    public getActiveAnnualReportWithReclasses$(): Observable<IAnnualReport> {
        return this.getActiveAnnualReport$().pipe(
            map((ar: IAnnualReport) => this.withTransactionalReclassesApplied(ar)),
        );
    }

    private reclassCache: WeakMap<IAnnualReport, IAnnualReport> = new WeakMap();
    protected withTransactionalReclassesApplied(ar: IAnnualReport | null | undefined): IAnnualReport {
        if (ar == null) return ar as unknown as IAnnualReport;
        if (ar.groupingAndMasking?.reclassificationsVersion !== 1) return ar;
        const cached = this.reclassCache.get(ar);
        if (cached != null) return cached;

        const clonedFinStatements: IFinStatements[] = Array.isArray(ar.finStatements)
            ? ar.finStatements.map((fs) => cloneDeep<IFinStatements>(fs))
            : ar.finStatements;
        applyReclassificationsToFinStatements(clonedFinStatements, ar.groupingAndMasking);
        // REV-20899: tag the bridged (display-only) statements so a save path
        // can never silently persist them (see assertPersistableFinStatements).
        if (Array.isArray(clonedFinStatements)) {
            clonedFinStatements.forEach(markReclassMaterialized);
        }

        const transformed: IAnnualReport = { ...ar, finStatements: clonedFinStatements };
        this.reclassCache.set(ar, transformed);
        return transformed;
    }
    public getAnnualReportIsSealed$(): Observable<boolean> {
        return this.store.pipe(select(fromFeature.selectActiveAnnualReportAccessibility));
    }
    public getActiveMetaInformation$(): Observable<AnnualReportMetaInformationDTO> {
        return this.store.pipe(select(fromFeature.selectActiveMetaInformationEntity));
    }
    public getMetaInformationByEncryptedId$(id: string): Observable<AnnualReportMetaInformationDTO> {
        return this.store.pipe(select(fromFeature.selectActiveMetaInformationById(id)));
    }

    getAnnualReportBalanceAutoFillId$(): Observable<string> {
        return this.store.pipe(select(fromFeature.selectAnnualReportForBalanceAutoFillId));
    }

    getAnnualReportForIncomeAutoFillId$(): Observable<string> {
        return this.store.pipe(select(fromFeature.selectAnnualReportForIncomeAutoFillId));
    }

    dispatchGetAnnualReportToTakeNotesStructureFrom(encryptedId: string): void {
        this.store.dispatch(fromAnnualReportAction.getAnnualReportToCopyNotesStructureFrom({ payload: encryptedId }));
    }

    getAnnualReportForUploadAndMappingAutoFill$(): Observable<IAnnualReport> {
        return this.store.pipe(
            select(fromFeature.selectAnnualReportForUploadAndMappingAutoFill),
            mergeMap((ar) => {
                if (!ar) {
                    this.store.pipe(
                        select(fromFeature.selectAnnualReportForBalanceAutoFillId),
                        filter((encryptedId) => encryptedId != null),
                        tap((encryptedId) => {
                            this.dispatchBalanceAutoFillFromChosenOrSavedId(encryptedId);
                        }),
                        map(() => null),
                    );
                }
                return of(ar);
            }),
        );
    }

    public dispatchActiveAnnualReportsList(filter?: IDispatchAnnualReportsFilter): void {
        this.store.dispatch(fromAnnualReportAction.getActiveAnnualReports({ payload: { filter } }));
    }

    public dispatchAnnualReportsListToCopyFrom(): void {
        this.store.dispatch(fromAnnualReportAction.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFrom());
    }

    public getAnnualReportsListToCopyFrom$(): Observable<AnnualReportMetaInformationDTO[]> {
        return this.store.select(fromFeature.selectAnnualReportsListToCopyFrom);
    }

    public dispatchArchivedAnnualReportsList(filter?: IDispatchAnnualReportsFilter): void {
        this.store.dispatch(fromAnnualReportAction.getArchivedAnnualReports({ payload: { filter } }));
    }

    public getAnnualReports$(): Observable<AnnualReportMetaInformationDTO[]> {
        return this.store.pipe(select(fromFeature.selectAllAnnualReports));
    }

    public dispatchActiveAnnualReport(annualReportId: string) {
        this.store.dispatch(fromAnnualReportAction.getActiveAnnualReport({ payload: annualReportId }));
    }

    public dispatchBalanceAutoFillFromChosenOrSavedId(annualReportId: string) {
        this.store.dispatch(fromAnnualReportAction.getBalanceAutofill({ payload: annualReportId }));
    }

    public dispatchIncomeAutoFillFromChosenOrSavedId(annualReportId: string) {
        this.store.dispatch(fromAnnualReportAction.getIncomeAutofill({ payload: annualReportId }));
    }

    setBalanceAutoFillIdWithChosenAnnualReportId(annualReportId: string) {
        this.store.dispatch(
            fromAnnualReportAction.setBalanceAutoFillIdWithChosenAnnualReportId({ payload: annualReportId }),
        );
    }

    setIncomeAutoFillIdWithChosenAnnualReportId(annualReportId: string) {
        this.store.dispatch(
            fromAnnualReportAction.setIncomeAutoFillIdWithChosenAnnualReportId({ payload: annualReportId }),
        );
    }

    resetBalanceAutoFillId() {
        this.store.dispatch(fromAnnualReportAction.resetBalanceAutoFillId());
    }

    resetIncomeAutoFillId() {
        this.store.dispatch(fromAnnualReportAction.resetIncomeAutoFillId());
    }

    resetFcp() {
        this.store.dispatch(fromAnnualReportAction.resetFcp());
    }

    public resetAnnualReportForBalanceAutoFill(): void {
        this.store.dispatch(fromAnnualReportAction.resetAnnualReportForBalanceAutoFill());
    }

    public resetAnnualReportForIncomeAutoFill(): void {
        this.store.dispatch(fromAnnualReportAction.resetAnnualReportForIncomeAutoFill());
    }

    public resetActiveAnnualReport(): void {
        this.store.dispatch(fromAnnualReportAction.resetActiveAnnualReport());
    }

    removeActiveAnnualReport() {
        this.store.dispatch(fromAnnualReportAction.removeActiveAnnualReport());
    }

    dispatchGetPreviousAnnualReportOptions(
        companyId: string,
        currentAnnualReportId: string,
        legalFormId: string,
        accountingBasisId?: string,
        locale?: string,
    ): void {
        this.store.dispatch(
            fromAnnualReportAction.getPreviousAnnualReportOptions({
                companyId,
                currentAnnualReportId,
                legalFormId,
                accountingBasisId,
                locale,
            }),
        );
    }

    dispatchGetAnnualReportsByCompanyId(companyId: string): void {
        this.store.dispatch(fromAnnualReportAction.getAnnualReportsByCompanyId({ payload: companyId }));
    }

    getAnnualReportForBalanceAutoFill$(step?: AutofillOptions): Observable<IAnnualReport> {
        return this.store.select(fromFeature.selectAnnualReportForBalanceAutoFill).pipe(
            switchMap((ar) => {
                if (!ar) {
                    this.store.pipe(select(fromFeature.selectAnnualReportForBalanceAutoFillId)).pipe(
                        tap((id) => {
                            this.store.dispatch(fromAnnualReportAction.getBalanceAutofill({ payload: id }));
                        }),
                        map(() => null),
                    );
                }
                return of(ar);
            }),
        );
    }

    getAnnualReportForIncomeAutoFill$(step?: AutofillOptions): Observable<IAnnualReport> {
        return this.store.select(fromFeature.selectAnnualReportForIncomeAutoFill).pipe(
            switchMap((ar) => {
                if (!ar) {
                    this.store.pipe(select(fromFeature.selectAnnualReportForIncomeAutoFillId)).pipe(
                        tap((id) => {
                            this.store.dispatch(fromAnnualReportAction.getIncomeAutofill({ payload: id }));
                        }),
                        map(() => null),
                    );
                }
                return of(ar);
            }),
        );
    }

    public getAnnualReportForBalanceAutoFillFromCache$(): Observable<IAnnualReport | null> {
        return this.store.select(fromFeature.selectAnnualReportForBalanceAutoFill).pipe(
            switchMap((report) => {
                if (report) return of(report);
                return this.getAnnualReportForBalanceAutoFill$();
            }),
        );
    }

    public getAnnualReportForIncomeAutoFillFromCache$(): Observable<IAnnualReport | null> {
        return this.store.select(fromFeature.selectAnnualReportForIncomeAutoFill).pipe(
            switchMap((report) => {
                if (report) return of(report);
                return this.getAnnualReportForIncomeAutoFill$();
            }),
        );
    }

    public getActiveAnnualReportFromCache$(): Observable<IAnnualReport | null> {
        return this.store.select(fromFeature.selectActiveAnnualReport).pipe(
            switchMap((report) => {
                if (report) return of(report);
                return this.getActiveAnnualReport$();
            }),
        );
    }

    public setActiveAnnualReport(annualReport: IAnnualReport) {
        this.store.dispatch(fromAnnualReportAction.setActiveAnnualReport({ payload: annualReport }));
    }

    public setActiveAnnualReportEncryptedId(annualReportEncryptedId: string) {
        this.store.dispatch(
            fromAnnualReportAction.setActiveAnnualReportEncryptedId({ payload: annualReportEncryptedId }),
        );
    }

    getActiveAnnualReportEncryptedId$(): Observable<string> {
        return this.store.select(fromFeature.selectActiveAnnualReportEncryptedId);
    }

    public setBalanceFinancialStatementSource(balanceFinancialStatementSource: IFinancialStatementSource): void {
        this.store.dispatch(
            fromAnnualReportAction.setBalanceFinancialStatementSource({ payload: balanceFinancialStatementSource }),
        );
    }

    public getBalanceFinancialStatementSource$(): Observable<IFinancialStatementSource> {
        return this.store.select(fromFeature.selectBalanceFinancialStatementSource);
    }

    public setIncomeFinancialStatementSource(incomeFinancialStatementSource: IFinancialStatementSource): void {
        this.store.dispatch(
            fromAnnualReportAction.setIncomeFinancialStatementSource({ payload: incomeFinancialStatementSource }),
        );
    }

    public getIncomeFinancialStatementSource$(): Observable<IFinancialStatementSource> {
        return this.store.select(fromFeature.selectIncomeFinancialStatementSource);
    }

    public dispatchArchiveAnnualReports(ids: string[]): void {
        this.store.dispatch(fromAnnualReportAction.archiveAnnualReports({ annualReportIds: ids }));
    }

    public dispatchRestoreAnnualReports(ids: string[]): void {
        this.store.dispatch(fromAnnualReportAction.restoreAnnualReports({ annualReportIds: ids }));
    }

    public dispatchDeleteAnnualReports(ids: string[]): void {
        this.store.dispatch(fromAnnualReportAction.deleteAnnualReports({ annualReportIds: ids }));
    }

    public isAnnualReportUrlIdValid$(id: string): Observable<boolean> {
        return this.store.select(fromFeature.isAnnualReportUrlIdValid(id));
    }

    public getError$(): Observable<HttpErrorResponse> {
        return this.store.select(fromFeature.selectError);
    }

    public flush(): void {
        this.store.dispatch(fromAnnualReportAction.flush());
    }

    public getFinancialStatmentImportLoseFocusChange$(): Observable<{} | undefined> {
        // making it a Subject instead of BehaviorSubject
        return this.store.select(fromFeature.selectFinancialStatementImportLoseFocus).pipe(skip(1));
    }

    public dispatchFinancialStatmentImportLoseFocus(): void {
        this.store.dispatch(fromAnnualReportAction.triggerFinancialStatementImportLoseFocus());
    }

    public resetAnnualReports(): void {
        debug('resetAR');
        this.store.dispatch(fromAnnualReportAction.resetAnnualReports());
    }

    public dispatchPrintView(printView: PrintUsingPdfTronDto): void {
        this.store.dispatch(fromAnnualReportAction.setPrintView({ payload: printView }));
    }

    public getPrintView$(): Observable<PrintUsingPdfTronDto> {
        return this.store.select(fromFeature.selectPrintView);
    }

    // responds the originally originally created fin statement produced in the mapping plus applying all changes of the closing process.
    public getActiveFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsAdjusted);
    }

    /**
     * REV-20899: same as `getActiveFinStatements$()` but with the transactional
     * reclassifications materialized onto each account's `openingReClass` /
     * `closingReClass`. Use this from any report step that needs to *show*
     * reclass-aware values (Grouping/Renaming, Cashflow, Notes, AoP, Equity
     * Movement, Fixed Assets, Print Preview). Do NOT use from Upload or
     * Account-Mapping — those steps must show raw uploaded balances.
     *
     * The output is cached per (finStatements ref, reclassifications ref) so
     * re-emissions on unchanged state don't re-clone.
     */
    public getActiveFinStatementsWithReclasses$(): Observable<IFinStatements[]> {
        return combineLatest([
            this.store.select(fromFeature.finStatmentsAdjusted),
            this.store.select(fromFeature.selectActiveAnnualReport),
        ]).pipe(
            map(([finStatements, ar]: [IFinStatements[], IAnnualReport | null]) => {
                if (!Array.isArray(finStatements)) return finStatements;
                if (ar?.groupingAndMasking?.reclassificationsVersion !== 1) return finStatements;
                const txnsRef: unknown = ar.groupingAndMasking.reclassifications ?? null;
                const cached = this.finStatementsReclassCache.get(finStatements);
                if (cached != null && cached.txnsRef === txnsRef) return cached.result;
                const cloned = finStatements.map((fs) => cloneDeep<IFinStatements>(fs));
                applyReclassificationsToFinStatements(cloned, ar.groupingAndMasking);
                // REV-20899: tag the bridged (display-only) statements so a
                // save path can never silently persist them.
                cloned.forEach(markReclassMaterialized);
                this.finStatementsReclassCache.set(finStatements, { txnsRef, result: cloned });
                return cloned;
            }),
        );
    }

    /**
     * REV-20899: caches the reclass-applied finStatements per source array
     * reference. NgRx selectors memoize and return the same reference until
     * the state slice changes, so this WeakMap effectively caches one entry
     * per "logical" finStatements value and an entry is GC'd when the store
     * replaces the array reference.
     */
    private finStatementsReclassCache: WeakMap<IFinStatements[], { txnsRef: unknown; result: IFinStatements[] }> = new WeakMap();
    // responds the originally originally created fin statement produced in the mapping plus applying all changes of the closing process from previous year
    public getBalanceAutofillFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsAdjustedOfBalanceAutofill);
    }

    public getIncomeAutofillFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsAdjustedOfIncomeAutofill);
    }

    public getCombinedAutofillFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsAdjustedForCombinedAutofill);
    }

    public getAnnualProfitOfBalanceAutofillFinstatements$(previousBalanceSheetDate: string): Observable<number | null> {
        return this.store.select(fromFeature.getAnnualProfitOfBalanceAutofillFinstatement(previousBalanceSheetDate));
    }


    public getGroupingMaskingForBalanceAutofill$(): Observable<any> {
        return this.store.select(fromFeature.groupingMaskingForBalanceAutoFill);
    }
    public getGroupingMaskingForIncomeAutofill$(): Observable<any> {
        return this.store.select(fromFeature.groupingMaskingForIncomeAutoFill);
    }
    public getGroupingMaskingForCopyAutofill$(): Observable<any> {
        return this.store.select(fromFeature.groupingMaskingForCopy);
    }

    // responds the originally originally created fin statement produced in the mapping plus applying all changes of the closing process from selected year
    public getSelectedYearForUploadAndMappingAutofillFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsAdjustedOfSelectedYearForUploadAndMappingAutofill);
    }

    // responds the originally originally created fin statement produced in the mapping
    public getActiveOriginalFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.finStatmentsOriginal);
    }

    public getFcpInputFinStatements$(): Observable<IFinStatements[]> {
        return this.store.select(fromFeature.selectFcpInputFinStatementsFromActiveAnnualReport);
    }
    public getActiveDueDates$(): Observable<DueDates | null> {
        return this.store.select(fromFeature.selectActiveDueDates);
    }

    public dispatchGetAnnualReportForMappingAutoFill(encryptedId: string): void {
        this.store.dispatch(fromAnnualReportAction.getAnnualReportForMappingAutoFill({ payload: encryptedId }));
    }

    public getAnnualReportToCopyNotesStructureFrom$(): Observable<IAnnualReport> {
        return this.store.pipe(select(fromFeature.selectAnnualReportToCopyNotesStructureFrom));
    }

    public dispatchGetNextBalanceSheetDateForCompanyById(companyId: string, reportType: ReportType): void {
        this.store.dispatch(
            fromAnnualReportAction.getNextBalanceSheetDateForCompanyById.getNextBalanceSheetDateForCompanyById({
                payload: {
                    companyId,
                    reportType,
                },
            }),
        );
    }

    getPreviousAnnualReportOptions$(): Observable<Partial<IAnnualReport>[] | null> {
        return this.store.pipe(select(fromFeature.selectPreviousAnnualReportOptions));
    }

    public dispatchPatchActiveAnnualReportIntegrations(integrations: IAnnualReport['integrations']): void {
        this.store.dispatch(fromAnnualReportAction.patchActiveAnnualReportIntegrations({ integrations }));
    }
}
